import { ResponsiveLine } from '@nivo/line';
import { linearGradientDef } from '@nivo/core';
import { format } from 'date-fns';
import styled from 'styled-components';
import { isset } from '../../../helpers';

const LegendSymbolLine = ({
    id,
    x,
    y,
    size,
    fill,
    borderWidth,
    borderColor,
}) => {
    if (id === 'huidige periode') {
        return (
            <rect
                x={x}
                y={y}
                width={size}
                height={size}
                stroke="none"
                fill={fill}
                style={{ pointerEvents: 'none' }}
            />
        );
    }
    return (
        <line
            x1={x}
            y1={y + size / 2 - 1}
            x2={x + size}
            y2={y + size / 2 - 1}
            strokeWidth={2}
            stroke={fill}
            style={{ pointerEvents: 'none' }}
        />
    );
};

const CustomLines = ({
    series,
    lineGenerator,
    xScale,
    yScale,
    height,
    margin,
}) => {
    return series.map(({ id, data, color, lineStyle, background }) => {
        if (background) {
            const minX = Math.min(...data.map((d) => d.data.x));
            const maxX = Math.max(...data.map((d) => d.data.x));
            return (
                <g key={id}>
                    <path
                        d={lineGenerator(
                            data.map((d) => ({
                                x: xScale(d.data.x),
                                y: yScale(d.data.y),
                            }))
                        )}
                        fill="none"
                        stroke={color}
                        strokeWidth={2}
                        style={lineStyle}
                    />
                    <rect
                        x={xScale(minX)}
                        width={xScale(maxX) - xScale(minX)}
                        height={height - margin.bottom - margin.top}
                        fill={background}
                    />
                </g>
            );
        }
        return (
            <path
                key={id}
                d={lineGenerator(
                    data.map((d) => ({
                        x: xScale(d.data.x),
                        y: yScale(d.data.y),
                    }))
                )}
                fill="none"
                stroke={color}
                strokeWidth={2}
                style={lineStyle}
            />
        );
    });
};

// const BackgroundLayer = (props) => {
//     return (
//         <g>
//             {/* <rect
//                 y={props.yScale(0.2)}
//                 width={props.width}
//                 height={props.yScale(0.1) - props.yScale(0.2)}
//                 fill="rgba(255,0,0,.15)"
//             /> */}
//             <rect
//                 x={props.xScale(new Date(2019, 0, 7, 12, 30, 0, 0, 0))}
//                 width={
//                     props.xScale(new Date(2019, 0, 14, 12, 30, 0, 0, 0)) -
//                     props.xScale(new Date(2019, 0, 7, 12, 30, 0, 0, 0))
//                 }
//                 height={props.height - props.margin.bottom - props.margin.top}
//                 fill="rgba(255,255,0,.15)"
//             />
//         </g>
//     );
// };

const TooltipContainer = styled.div`
    background: #fff;
    padding: 2px 8px;
    box-shadow: 1px 2px 6px 2px #888888;
`;
const Tooltip = ({ point }) => {
    const { data, serieColor } = point;
    const { x, y, originalY } = data;
    const yVal = 1 * (isset(originalY) ? originalY : y);
    return (
        <TooltipContainer>
            {format(x, 'd-M H:mm')}{' '}
            <strong style={{ color: serieColor }}>
                {Math.round(yVal * 100) / 100}
            </strong>
        </TooltipContainer>
    );
};

const SeriesColorSymbol = styled.div`
    width: 12px;
    height: 12px;
    background-color: ${({ color }) => color};
`;
const SliceTooltip = ({ slice }) => {
    const { points } = slice;
    return (
        <TooltipContainer>
            {points.length && format(points[0].data.x, 'd-M H:mm')}{' '}
            <table>
                <tbody>
                    {points.map((point) => {
                        const { data, serieColor, serieId, id } = point;
                        const { y, originalY } = data;
                        const yVal = 1 * (isset(originalY) ? originalY : y);

                        return (
                            <tr key={id}>
                                <td>
                                    <SeriesColorSymbol color={serieColor} />
                                </td>
                                <td>{serieId} </td>
                                <td style={{ textAlign: 'right' }}>
                                    {yVal.toFixed(2)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </TooltipContainer>
    );
};

const MainContainer = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    padding-top: ${({ hasTitle }) => (hasTitle ? 16 : 4)}px;
`;
const Title = styled.div`
    position: absolute;
    top: 0px;
    left: 40px;
`;

// make sure parent container have a defined height when using
// responsive component, otherwise height will be 0 and
// no chart will be rendered.
const ResponsiveLineChart = ({
    data = [],
    xScaleMin,
    xScaleMax,
    yScaleMin = 0,
    enableArea = true,
    dateTicks,
    title,
    normalise = false,
}) => {
    let colors = data.map((d) => d.color).filter((d) => d);
    if (colors.length < 1) {
        colors = { scheme: 'category10' };
    }

    const chartData = normalise
        ? data.map((line) => {
              if (line.background) {
                  return line;
              }
              const yRange = line.data.reduce(
                  (prev, cur) => {
                      return [
                          prev[0] === null ? cur.y : Math.min(cur.y, prev[0]),
                          prev[1] === null ? cur.y : Math.max(cur.y, prev[1]),
                      ];
                  },
                  [null, null]
              );

              return {
                  ...line,
                  data: line.data.map((d) => ({
                      ...d,
                      originalY: d.y,
                      y: (d.y - yRange[0]) / (yRange[1] - yRange[0]),
                  })),
              };
          })
        : data;

    return (
        <MainContainer hasTitle={title}>
            {title && <Title>{title}</Title>}
            <ResponsiveLine
                data={chartData}
                colors={colors}
                margin={{
                    top: 20,
                    right: 40,
                    bottom: 40,
                    left: 40,
                }}
                xScale={{
                    type: 'time',
                    // format: '%Y-%m-%d %H:%M:%S',
                    format: 'native',
                    useUTC: false,
                    precision: 'second',
                    min: xScaleMin,
                    max: xScaleMax,
                }}
                yScale={{
                    type: 'linear',
                    min: yScaleMin,
                    max: 'auto',
                    stacked: false,
                    reverse: false,
                }}
                // yFormat=" >-.2f"
                axisTop={null}
                axisRight={null}
                axisBottom={{
                    format: '%d %b',
                    // tickValues: 'every 0.5 months',
                    // tickValues: 'every 1 days',
                    // tickValues: dateTicks?.filter((d, i) => i % 2 === 0),
                    tickValues: dateTicks || 6,
                    // tickValues: 7,
                    tickRotation: 30,
                    // legend: 'time scale',
                    // legendOffset: -12,
                }}
                gridXValues={dateTicks || 6}
                // axisBottom={{
                //     orient: 'bottom',
                //     tickSize: 5,
                //     tickPadding: 5,
                //     tickRotation: 0,
                //     // legend: 'date',
                //     legendOffset: 36,
                //     legendPosition: 'middle',
                // }}
                axisLeft={
                    normalise
                        ? null
                        : {
                              orient: 'left',
                              tickSize: 5,
                              tickPadding: 5,
                              tickRotation: 0,
                              // legend: 'count', //unit
                              legendOffset: -40,
                              legendPosition: 'middle',
                          }
                }
                pointSize={0}
                // pointColor={{ theme: 'background' }}
                // pointBorderWidth={2}
                // pointBorderColor={{ from: 'serieColor' }}
                pointLabelYOffset={-12}
                useMesh={true}
                legends={[
                    {
                        anchor: 'top',
                        direction: 'row',
                        justify: false,
                        // translateX: 100,
                        translateY: -20,
                        itemsSpacing: 0,
                        itemDirection: 'left-to-right',
                        itemWidth: 130,
                        itemHeight: 20,
                        itemOpacity: 1,
                        symbolSize: 12,
                        symbolShape: LegendSymbolLine,
                        symbolBorderColor: 'rgba(0, 0, 0, .5)',
                        // effects: [
                        //     {
                        //         on: 'hover',
                        //         style: {
                        //             itemBackground: 'rgba(0, 0, 0, .03)',
                        //             itemOpacity: 1,
                        //         },
                        //     },
                        // ],
                    },
                ]}
                tooltip={Tooltip}
                enableSlices={chartData.length > 1 ? 'x' : false}
                sliceTooltip={SliceTooltip}
                defs={[
                    linearGradientDef('gradientA', [
                        { offset: 0, color: 'inherit' },
                        { offset: 100, color: 'inherit', opacity: 0 },
                    ]),
                ]}
                enableArea={enableArea}
                fill={[{ match: '*', id: 'gradientA' }]}
                areaBaselineValue={yScaleMin}
                layers={[
                    'grid',
                    'markers',
                    'axes',
                    'areas',
                    'crosshair',
                    // 'lines',
                    CustomLines,
                    'points',
                    'slices',
                    'mesh',
                    'legends',
                ]}
            />
        </MainContainer>
    );
};

export default ResponsiveLineChart;
